import React, { useEffect, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ReactComponent as MapBackPlayIcon } from '~/assets/icons/map-back-play.svg';
import { ReactComponent as MapBackPauseIcon } from '~/assets/icons/map-back-pause.svg';
import styles from '../Journey.module.scss';
import { PLAYBACK_SPEED_OPTIONS, PlaybackSpeed } from '../constants/journey';

interface PlaybackControllerProps {
  totalSegments: number;
  currentSegmentIndex: number;
  onSegmentChange: (newIndex: number) => void;
  isPlaying: boolean;
  onPlayChange: (isPlaying: boolean) => void;
  playSpeed: PlaybackSpeed;
  onSpeedChange: (speed: PlaybackSpeed) => void;
  isLoading?: boolean;
}

const PlaybackController: React.FC<PlaybackControllerProps> = ({
  totalSegments,
  currentSegmentIndex,
  onSegmentChange,
  isPlaying,
  onPlayChange,
  playSpeed,
  onSpeedChange,
  isLoading = false,
}) => {
  const { t } = useTranslation('admin/journey');
  const hasSegments = totalSegments > 0;
  const maxIndex = Math.max(0, totalSegments - 1);
  const safeCurrentIndex = Math.min(Math.max(currentSegmentIndex, 0), maxIndex);
  const [isSpeedMenuOpen, setIsSpeedMenuOpen] = useState(false);
  const speedMenuRef = useRef<HTMLDivElement | null>(null);
  const rangeRef = useRef<HTMLInputElement | null>(null);
  const [rangeWidth, setRangeWidth] = useState(0);
  const progressRatio = hasSegments && maxIndex > 0 ? Math.min(Math.max(safeCurrentIndex / maxIndex, 0), 1) : 0;
  const thumbSize = 18;
  const progressStopPx = rangeWidth > thumbSize ? thumbSize / 2 + progressRatio * (rangeWidth - thumbSize) : 0;

  useEffect(() => {
    if (!isSpeedMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!speedMenuRef.current?.contains(event.target as Node)) {
        setIsSpeedMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSpeedMenuOpen]);

  useEffect(() => {
    const inputEl = rangeRef.current;
    if (!inputEl) return;

    const updateWidth = () => setRangeWidth(inputEl.clientWidth);
    updateWidth();

    const observer = new ResizeObserver(() => updateWidth());
    observer.observe(inputEl);

    return () => observer.disconnect();
  }, []);

  return (
    <Box className='h-[52px] rounded-b-[8px] bg-[#161A1F] px-4 text-white'>
      <Box className='flex h-full flex-nowrap items-center gap-4'>
        <Button
          variant='text'
          className='flex h-10 w-10 normal-case min-w-0 flex-shrink-0 items-center justify-center rounded-[100px] bg-[#7C87961A] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50'
          onClick={() => onPlayChange(!isPlaying)}
          disabled={isLoading || !hasSegments}
          aria-label={isPlaying ? t('timeline.pause') : t('timeline.play')}
        >
          {isPlaying ? (
            <MapBackPauseIcon className='h-4 w-4 [filter:brightness(0)_invert(1)]' />
          ) : (
            <MapBackPlayIcon className='h-4 w-4 [filter:brightness(0)_invert(1)]' />
          )}
        </Button>

        <Box className='min-w-0 flex-1'>
          <input
            ref={rangeRef}
            type='range'
            min={0}
            max={maxIndex}
            step={0.001}
            value={safeCurrentIndex}
            onChange={(e) => onSegmentChange(Number(e.target.value))}
            disabled={isLoading || !hasSegments}
            className={styles.playbackRange}
            style={{
              background: `linear-gradient(to right, #2E89C8 0px, #2E89C8 ${progressStopPx}px, #FFFFFF ${progressStopPx}px, #FFFFFF 100%)`,
            }}
          />
        </Box>

        <Box ref={speedMenuRef} className='relative ml-auto flex flex-shrink-0 items-center'>
          <Button
            variant='text'
            onClick={() => setIsSpeedMenuOpen((prev) => !prev)}
            disabled={isLoading}
            className='flex h-8 min-w-[40px] normal-case items-center justify-center rounded-md bg-transparent px-2 text-sm font-semibold leading-none text-white disabled:cursor-not-allowed disabled:opacity-50'
          >
            {playSpeed}x
          </Button>

          {isSpeedMenuOpen && (
            <Box className='absolute bottom-[36px] right-0 z-20 w-[58px] overflow-hidden rounded-lg border border-[#E3E7EC] bg-white py-1 shadow-[0px_6px_16px_rgba(0,0,0,0.25)]'>
              {PLAYBACK_SPEED_OPTIONS.map((speed) => {
                const active = playSpeed === speed;
                return (
                  <Button
                    key={speed}
                    variant='text'
                    onClick={() => {
                      onSpeedChange(speed);
                      setIsSpeedMenuOpen(false);
                    }}
                    className={`flex h-7 w-full normal-case min-w-0 items-center justify-center text-xs leading-none ${
                      active ? 'bg-[#F3F6F9] text-[#161A1F]' : 'text-[#161A1F] hover:bg-[#F3F6F9]'
                    }`}
                  >
                    {speed}x
                  </Button>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(PlaybackController);
